import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, TrashIcon } from 'lucide-react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogFooter } from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { OAuthServerMetadata } from '@/interfaces/OAuth'
import { API_URL } from '@/lib/api'

const keys = 'settings.api.oauthClients.form'

const isAllowedRedirectUri = (value: string) => {
  try {
    const url = new URL(value)

    return url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname))
  } catch {
    return false
  }
}

const schema = z
  .object({
    name: z.string().trim().min(1, `${keys}.name.errors.required`),
    redirectUris: z.array(
      z.object({ value: z.string().trim().refine(isAllowedRedirectUri, `${keys}.redirectUris.errors.invalid`) }),
    ),
    scopes: z.array(z.string()).min(1, `${keys}.scopes.errors.required`),
    serverToServer: z.boolean(),
  })
  // A pure machine client never uses the redirect, so it needs no URI.
  .refine((data) => data.serverToServer || data.redirectUris.length > 0, {
    path: ['redirectUris'],
    message: `${keys}.redirectUris.errors.required`,
  })

export type OAuthClientFormData = z.infer<typeof schema>

export type OAuthClientPayload = {
  name: string
  redirectUris: string[]
  scopes: string[]
  serverToServer: boolean
}

export function OAuthClientForm({
  defaultValues,
  onSubmit,
  onCancel,
}: {
  defaultValues?: OAuthClientPayload
  onSubmit: (payload: OAuthClientPayload) => Promise<unknown>
  onCancel: () => void
}) {
  const { t: tr } = useTranslation()
  const { t: tg } = useTranslation(undefined, { keyPrefix: 'generic' })

  // Plain fetch without cookies: the endpoint allows any origin (*), and browsers block * for requests with credentials.
  const { data: metadata } = useSWR<OAuthServerMetadata>(API_URL + '/.well-known/oauth-authorization-server', (url: string) =>
    fetch(url).then((res) => res.json()),
  )

  const form = useForm<OAuthClientFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: defaultValues?.name ?? '',
      redirectUris: (defaultValues?.redirectUris ?? ['']).map((value) => ({ value })),
      scopes: defaultValues?.scopes ?? [],
      serverToServer: defaultValues?.serverToServer ?? false,
    },
  })

  const redirectUris = useFieldArray({ control: form.control, name: 'redirectUris' })

  // "invoices:read", "invoices:write" → one row per area with read and write.
  const areas = Object.entries(
    Object.groupBy(metadata?.scopes_supported ?? [], (scope) => scope.split(':')[0]),
  )

  const submit = (data: OAuthClientFormData) =>
    onSubmit({ ...data, redirectUris: data.redirectUris.map((uri) => uri.value) })


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="space-y-5">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tr(`${keys}.name.label`)}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={tr(`${keys}.name.placeholder`)} autoComplete="off" autoFocus />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Not a FormField: the Form* helpers need one field, and this is a list. */}
        <div className="grid gap-2">
          <Label>{tr(`${keys}.redirectUris.label`)}</Label>
          <div className="space-y-2">
            {redirectUris.fields.map((item, index) => (
              <FormField
                key={item.id}
                control={form.control}
                name={`redirectUris.${index}.value`}
                render={({ field }) => (
                  <FormItem>
                    <div className="flex gap-1">
                      <FormControl>
                        <Input {...field} type="url" placeholder="https://example.com/oauth/callback" />
                      </FormControl>
                      <Button type="button" variant="ghost" size="icon" onClick={() => redirectUris.remove(index)}>
                        <TrashIcon />
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => redirectUris.append({ value: '' })}>
              <Plus />
              {tr(`${keys}.redirectUris.add`)}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">{tr(`${keys}.redirectUris.description`)}</p>
          {/* The resolver puts the error of a field array under root. */}
          {form.formState.errors.redirectUris?.root?.message && (
            <p className="text-sm text-danger">{tr(form.formState.errors.redirectUris.root.message)}</p>
          )}
        </div>

        <FormField
          control={form.control}
          name="scopes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tr(`${keys}.scopes.label`)}</FormLabel>
              <div className="divide-y rounded-lg border">
                {areas.map(([area, scopes]) => (
                  <div key={area} className="flex min-h-10 items-center gap-4 px-3 text-sm">
                    <span className="flex-1">{tr(`oauth.scopes.areas.${area}`, { defaultValue: area })}</span>
                    {scopes?.map((scope) => (
                      <label key={scope} className="flex items-center gap-1.5 text-muted-foreground">
                        <Checkbox
                          checked={field.value.includes(scope)}
                          onCheckedChange={(checked) =>
                            field.onChange(checked ? [...field.value, scope] : field.value.filter((s) => s !== scope))
                          }
                        />
                        {tr(`oauth.scopes.access.${scope.split(':')[1]}`, { defaultValue: scope })}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <FormDescription>{tr(`${keys}.scopes.description`)}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="serverToServer"
          render={({ field }) => (
            <FormItem>
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox checked={field.value} onCheckedChange={(checked) => field.onChange(checked === true)} />
                {tr(`${keys}.serverToServer.label`)}
              </label>
              <FormDescription>{tr(`${keys}.serverToServer.description`)}</FormDescription>
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {tg('button.cancel')}
          </Button>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {tg('button.save')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
